import { ref, reactive, inject, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import donationStatusOptions from '@/services/donations/options'
import { sendCCInfo } from '@/services/donor/'
import { DonationApi } from '@/services/donations/'
import { DonorApi } from '../services/donor'
import {
  EngagingNetwork,
  SpringBoard,
  paymentProviders,
  NAACPCustomAPI,
  BBMSCheckout,
  BBMSCheckoutInline,
  BBMSSkyPayments,
} from '@/services/npo/'
import {
  closeActiveCheckoutModal,
  openCheckoutModal,
  parseCheckoutAmount,
  parseDonationResponse,
  submitDonateCheckout,
} from '@/services/donor/bbmsCheckout'
import { executeRecaptcha } from '@/services/donor/recaptcha'
import { termsAgreementField } from '../services/users'
import { formatTransactionIdMessage } from '@/utils/misc'
import { clearStoredFormToken, resolveFormToken } from '@/services/formAccess'

export function donationActions() {
  const donationStatuses = donationStatusOptions.DONATION_STATUSES
  const campaign = ref(null)
  const successModalOpen = ref(false)
  const failureModalOpen = ref(false)
  const route = useRoute()
  const campaignId = route.params.id
  // Anonymous QR devices carry a signed grant instead of a fundraiser id; the
  // backend derives attribution from it, so `fundraiserId` stays empty there.
  const formToken = resolveFormToken(route)
  let fundraiserId = route.query.fundraiser
  let qrCode = route.query.from_qrcode
  const mockPayment = route.query.mockpayment
  const donateFailureMessage = ref('')
  const donorId = ref('')
  const loading = ref(false)
  const donateSuccessMessage = ref('')
  const $everyaction = inject('$everyaction')
  const termsAgreement = ref(termsAgreementField)
  const paymentProvider = ref(null)
  const submissionStatus = ref(null)
  const submissionId = ref(null)
  const failedAttempts = ref(0)
  const bbmsCheckoutFailedAttempts = ref(0)
  const bbmsCheckoutRetryMessage = ref('')
  const MAX_BBMS_CHECKOUT_ATTEMPTS = 2
  let resetDonationFormCallback = null
  // The inline checkout workflow starts saving the PENDING donation on button click and
  // may receive the transaction token before that save finishes, so the token handler
  // waits on this promise instead of racing it.
  let inlinePendingSave = null
  let inlineFailureContext = null

  const registerResetDonationForm = (fn) => {
    resetDonationFormCallback = fn
  }

  const QR_PAYMENT_CONNECTION_MSG =
    'Connection could not be established with the payment processor. Please try again later.'

  const isThirdPartyPendingResponse = (response) =>
    response?.donation_status === 'PENDING' || response?.donationStatus === 'PENDING'

  const isThirdPartyFailureResponse = (response) =>
    response?.donation_status === 'FAILURE' || response?.donationStatus === 'FAILURE'

  const normalizeQrCodeForApi = (qrCodeValue) => {
    if (qrCodeValue === true || qrCodeValue === 'true') return true
    if (qrCodeValue === false || qrCodeValue === 'false') return false
    return undefined
  }

  const resolveQrCodeForPayload = () => {
    const fromRoute = normalizeQrCodeForApi(qrCode)
    if (fromRoute !== undefined) return fromRoute
    const stored = localStorage.getItem('qrCode')
    if (stored !== null && stored !== '') {
      try {
        const parsed = JSON.parse(stored)
        const fromStorage = normalizeQrCodeForApi(parsed)
        if (fromStorage !== undefined) return fromStorage
      } catch {
        const fromStorage = normalizeQrCodeForApi(stored)
        if (fromStorage !== undefined) return fromStorage
      }
    }
    return false
  }

  const isQrCodeDonation = () => resolveQrCodeForPayload() === true

  const clearDonorSessionStorage = () => {
    localStorage.removeItem('DonorData')
    localStorage.removeItem('campaign')
    localStorage.removeItem('qrCode')
    localStorage.removeItem('fundraiserId')
    clearStoredFormToken()
  }

  const handleThirdPartyPendingResponse = () => {
    clearDonorSessionStorage()
    donateSuccessMessage.value =
      'The payment processor did not respond. This donation is saved as Pending — open your Donations list and click Resubmit to try again.'
    toggleSuccessModal()
    loading.value = false
    return true
  }

  const formatThirdPartyMessage = (message) => {
    if (!message) return ''
    if (typeof message === 'string') return message
    if (message?.error) return String(message.error)
    return JSON.stringify(message)
  }

  const isTechnicalConnectionError = (message) => {
    const text = String(message || '').toLowerCase()
    return (
      text.includes('nodename nor servname') ||
      text.includes('failed to resolve') ||
      text.includes('nameresolutionerror') ||
      text.includes('httpsconnectionpool') ||
      text.includes('connectionerror') ||
      text.includes('connecttimeout') ||
      text.includes('readtimeout') ||
      text.includes('name or service not known') ||
      text.includes('network is unreachable') ||
      text.includes('temporary failure in name resolution') ||
      text.includes('max retries exceeded')
    )
  }

  const shouldShowQrConnectionMessage = (response) => {
    const noResponse = response?.no_response === true || response?.noResponse === true
    const transactionErrorMsg =
      response?.transaction_error_msg || response?.transactionErrorMsg || ''
    const formattedMessage = formatThirdPartyMessage(response?.message)
    return (
      noResponse ||
      transactionErrorMsg === QR_PAYMENT_CONNECTION_MSG ||
      isTechnicalConnectionError(formattedMessage) ||
      isTechnicalConnectionError(transactionErrorMsg)
    )
  }

  const handleThirdPartyDonationResponse = (response) => {
    const noResponse = response?.no_response === true || response?.noResponse === true
    const transactionErrorMsg =
      response?.transaction_error_msg || response?.transactionErrorMsg || ''

    if (isQrCodeDonation() && shouldShowQrConnectionMessage(response)) {
      handleFailure('', '', QR_PAYMENT_CONNECTION_MSG)
      return true
    }
    if (isThirdPartyPendingResponse(response)) {
      return handleThirdPartyPendingResponse()
    }
    if (isQrCodeDonation() && isThirdPartyFailureResponse(response)) {
      const providerMsg = formatThirdPartyMessage(response?.message) || transactionErrorMsg
      handleFailure('', '', providerMsg || 'Donation failed.')
      return true
    }
    return false
  }

  const handleQrOrGenericSubmitFailure = (response, genericMessage) => {
    if (response && handleThirdPartyDonationResponse(response)) {
      return true
    }
    if (isQrCodeDonation() && response && shouldShowQrConnectionMessage(response)) {
      return handleFailure('', '', QR_PAYMENT_CONNECTION_MSG)
    }
    if (isQrCodeDonation()) {
      const providerMsg =
        formatThirdPartyMessage(response?.message) ||
        response?.transaction_error_msg ||
        response?.transactionErrorMsg
      return handleFailure('', '', providerMsg || genericMessage)
    }
    return handleFailure('', '', genericMessage)
  }

  const toggleSuccessModal = () => {
    paymentProvider.value = campaign.value.latestPaymentProvider?.paymentProvider

    if (
      paymentProviders[paymentProvider.value] === SpringBoard &&
      qrCode == 'true' &&
      successModalOpen.value
    ) {
      window.location.href = 'https://www.rescue.org/'
    } else if (
      paymentProviders[paymentProvider.value] === EngagingNetwork &&
      qrCode == 'true' &&
      successModalOpen.value
    ) {
      window.location.href = 'https://wwf.org/'
    } else if (
      paymentProviders[paymentProvider.value] === NAACPCustomAPI &&
      qrCode == 'true' &&
      successModalOpen.value
    ) {
      window.location.href = 'https://naacp.org/'
    } else if (
      paymentProviders[paymentProvider.value] === BBMSSkyPayments &&
      qrCode == 'true' &&
      successModalOpen.value
    ) {
      window.location.href = 'https://www.operationsmile.org/'
    } else if (successModalOpen.value) {
      location.reload()
    }
    successModalOpen.value = !successModalOpen.value
  }

  // Display failure modal if the donor fails to create
  const toggleFailureModal = () => {
    if (failedAttempts.value > 2) {
      location.reload()
    }
    failureModalOpen.value = !failureModalOpen.value
    failedAttempts.value += 1
  }

  const getMaskedPan = (cardNumber) => {
    if (!cardNumber || typeof cardNumber !== 'string') return ''
    const digits = cardNumber.replace(/\D/g, '')
    if (digits.length < 10) return ''
    const firstFour = digits.slice(0, 6)
    const lastFour = digits.slice(-4)
    return firstFour + '******' + lastFour
  }

  const generateErrorMsg = (e) => {
    // Order matters: try the most specific known shapes first, then fall
    // through to generic DRF, then a last-resort stringify so we never end
    // up showing "[object Object]" to a donor.
    let userMessage = e.message
    const responseData = e.response?.data

    // 1) Luminate Online / BBMSCheckout error shapes. Luminate replies with
    //    `donationResponse.errors.{fieldError, declineUserMessage, message, code}`
    //    depending on whether it's a validation, decline, or generic error.
    const errors = responseData?.donationResponse?.errors
    if (errors) {
      const fieldError = errors.fieldError
      if (Array.isArray(fieldError) && fieldError.length) {
        userMessage = fieldError.join(' ').trim()
      } else if (typeof fieldError === 'string' && fieldError.trim()) {
        userMessage = fieldError
      } else if (typeof errors.declineUserMessage === 'string' && errors.declineUserMessage) {
        userMessage = errors.declineUserMessage
      } else if (typeof errors.message === 'string' && errors.message) {
        userMessage = errors.code ? `${errors.message} (code ${errors.code})` : errors.message
      } else {
        // Unknown Luminate shape - serialize the errors block so it's at
        // least diagnosable instead of "[object Object]".
        try {
          userMessage = `donateCheckout error: ${JSON.stringify(errors)}`
        } catch (_) {
          userMessage = 'donateCheckout error (unparseable response)'
        }
      }
    } else if (responseData?.errorResponse) {
      // 2) Top-level errorResponse from our BBMS proxy (e.g. timeout, 5xx
      //    from Luminate with non-JSON body).
      const er = responseData.errorResponse
      userMessage = er.message || er.raw_body || 'donateCheckout failed.'
    } else if (responseData) {
      // 3) DRF validation errors. They come as either an array of strings,
      //    a single string, or a `{field: [msg]}` map.
      if (Array.isArray(responseData) && responseData.length) {
        userMessage = responseData.join(' ')
      } else if (typeof responseData === 'string') {
        userMessage = responseData
      } else if (typeof responseData === 'object') {
        if (responseData.detail) {
          userMessage = String(responseData.detail)
        } else if (responseData.message) {
          userMessage = String(responseData.message)
        } else {
          const parts = []
          for (const [field, value] of Object.entries(responseData)) {
            const text = Array.isArray(value) ? value.join(' ') : String(value)
            parts.push(`${field}: ${text}`)
          }
          if (parts.length) userMessage = parts.join(' | ')
        }
      }
    }

    if (e.response) {
      // Dump the full payload so engineers can see the exact Luminate /
      // Django reply when a donor reports a failure (otherwise we'd be
      // guessing at the schema every time it changes).
      try {
        console.error(
          '[donation] server error response:',
          e.response.status,
          JSON.stringify(responseData, null, 2),
        )
      } catch (_) {
        console.error('[donation] server error response:', e.response.status, responseData)
      }
    }
    return userMessage
  }

  const handleFailure = (label, error, customMessage) => {
    donateFailureMessage.value = customMessage ? customMessage : error.message
    toggleFailureModal()
    loading.value = false
  }

  const createTransaction = async (paymentDetails) => {
    let transactionInfo
    try {
      const transaction = await sendCCInfo(paymentDetails)
      transactionInfo = transaction.donationResponse.donation
    } catch (e) {
      donateFailureMessage.value = generateErrorMsg(e)
    }
    return transactionInfo
  }

  const createDonor = async () => {
    try {
      let data = JSON.parse(localStorage.getItem('DonorData'))
      data = data?.donor || data

      const donor = await DonorApi.csc.customCreate({
        ...data,
        formToken,
      })
      donorId.value = donor.id
    } catch (e) {
      console.log(e)
      return handleFailure('Failed to create donor', e)
    }
  }

  const buildFailureTransactionInfo = (uniqueId) => {
    const id = uniqueId || generateConfirmationCode()
    return {
      confirmationCode: `checkout-fail-${id}`,
      transactionId: '',
    }
  }

  const mapDonationAnswers = (questionData) => {
    return (questionData || [])
      .map((question) => question?.question?.answers?.[0]?.id)
      .filter(Boolean)
  }

  const resolveDonationFrequency = (data, frequency) => {
    const raw = frequency ?? data?.frequency
    if (raw && typeof raw === 'object') {
      return raw.id ?? raw.value?.id
    }
    if (raw && String(raw).trim().length) {
      return String(raw).trim()
    }
    return undefined
  }

  const saveBBMSCheckoutDonation = async (
    { campaignId, donationFormData, frequency, fundraiserId, questionData, paymentData, qrCode },
    { status, errorMessage = '', transactionInfo = null, isSuccess },
  ) => {
    if (!donorId.value) return

    const uniqueId = donationFormData.unique_id ?? donationFormData.uniqueId
    const resolvedTransactionInfo =
      transactionInfo || (isSuccess ? null : buildFailureTransactionInfo(uniqueId))

    await createDonation(
      campaignId,
      donationFormData,
      frequency,
      fundraiserId,
      questionData,
      resolvedTransactionInfo,
      paymentData,
      qrCode,
      {
        errorMessage,
        isSuccess,
        statusOverride: status,
        suppressFailureModal: true,
      },
    )
  }

  const createDonation = async (
    campaignId,
    data,
    frequency,
    fundraiserId,
    questionData,
    transactionInfo,
    paymentData,
    qrCode,
    { errorMessage = '', isSuccess, statusOverride, suppressFailureModal = false } = {},
  ) => {
    try {
      const answers = mapDonationAnswers(questionData)
      const maskedPan = getMaskedPan(paymentData?.card_number)
      const hasCardExp = paymentData?.card_exp_date_month && paymentData?.card_exp_date_year
      const uniqueId = data.unique_id ?? data.uniqueId
      const resolvedTransactionInfo =
        transactionInfo || (isSuccess === false ? buildFailureTransactionInfo(uniqueId) : null)
      const success =
        typeof isSuccess === 'boolean'
          ? isSuccess
          : Boolean(
              resolvedTransactionInfo?.transactionId || resolvedTransactionInfo?.confirmationCode,
            )
      const freq = resolveDonationFrequency(data, frequency)
      const amount = data.otherAmount ?? data['otherAmount']
      const normalizedQrCode = normalizeQrCodeForApi(qrCode)
      const donationInfo = {
        campaign: campaignId,
        cardBrand: data.cardBrand || '',
        ...(maskedPan ? { cardMaskedPan: maskedPan } : {}),
        donor: donorId.value,
        ...(resolvedTransactionInfo || {}),
        transactionErrorMsg: errorMessage || donateFailureMessage.value || '',
        ...(freq ? { frequency: freq } : {}),
        amount: amount != null && amount !== '' ? Number(amount) : amount,
        formToken,
        answers,
        status: statusOverride || (success ? donationStatuses.SUCCESS : donationStatuses.FAILURE),
        ...(normalizedQrCode !== undefined ? { qrCode: normalizedQrCode } : {}),
        uniqueId,
        ...(hasCardExp
          ? {
              cardExpirationDate:
                paymentData.card_exp_date_month + '/' + paymentData.card_exp_date_year,
            }
          : {}),
        ...(data.cardHolderName ? { cardHolderName: data.cardHolderName } : {}),
      }
      await DonationApi.csc.customCreate(donationInfo)
    } catch (e) {
      const apiDetail = e.response?.data
      if (apiDetail) {
        console.error('Donation create failed:', apiDetail)
      }
      if (suppressFailureModal) {
        throw e
      }
      return handleFailure('Failed to create donation', e)
    }
  }

  const handleBBMSCheckoutFailure = async (
    errorMessage,
    { campaignId, donationFormData, frequency, fundraiserId, questionData, paymentData, qrCode },
  ) => {
    closeActiveCheckoutModal()
    donateFailureMessage.value = errorMessage
    bbmsCheckoutRetryMessage.value = errorMessage

    try {
      await saveBBMSCheckoutDonation(
        {
          campaignId,
          donationFormData,
          frequency,
          fundraiserId,
          questionData,
          paymentData,
          qrCode,
        },
        { status: donationStatuses.FAILURE, errorMessage, isSuccess: false },
      )
    } catch (saveError) {
      console.error('Failed to save BBMS checkout failure record:', saveError)
    }

    bbmsCheckoutFailedAttempts.value += 1
    loading.value = false

    if (bbmsCheckoutFailedAttempts.value >= MAX_BBMS_CHECKOUT_ATTEMPTS) {
      bbmsCheckoutFailedAttempts.value = 0
      bbmsCheckoutRetryMessage.value = ''
      donorId.value = ''
      resetDonationFormCallback?.()
      donateFailureMessage.value = `${errorMessage} You have reached the maximum number of payment attempts. Please re-enter your information and try again.`
      failureModalOpen.value = true
    }
  }

  const generateConfirmationCode = () => {
    return Math.floor(100000000 + Math.random() * 900000000).toString()
  }

  const onNAACPCustomAPISubmitForm = async (data) => {
    loading.value = true
    const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }
    const payload = {
      firstName: donorData.donor.firstName,
      lastName: donorData.donor.lastName,
      mail: data['donor.email'],
      address: `${data['billing.address.street1']} ${data['billing.address.street2']}`.trim(),
      cardHolderName: `${data['billing.name.first']} ${data['billing.name.last']}`.trim(),
      city: data['billing.address.city'],
      mobilePhone: donorData.donor.mobilePhone,
      state: data['billing.address.state'],
      zip: data['billing.address.zip'],
      cardNumber: data['card_number'],
      cardCvv: data['card_cvv'],
      amount: data['otherAmount'],
      frequency: data['sustaining.frequency'],
      uniqueId: data['unique_id'],
      cardExpDateMonth: Number(data['card_exp_date_month']),
      cardExpDateYear: Number(data['card_exp_date_year']),
      campaign: campaignId,
      frequencyId: data['frequency'],
      formToken,
      confirmationCode: generateConfirmationCode(),
      qrCode: resolveQrCodeForPayload(),
      longitude: donorData.donor.longitude || 0,
      latitude: donorData.donor.latitude || 0,
      isEmailPreferred: donorData.donor.isEmailPreferred,
      isSmsPreferred: donorData.donor.isSmsPreferred,
      isPhoneCallPreferred: donorData.donor.isPhoneCallPreferred,
      dateOfBirth: donorData.donor.dateOfBirth,
      donorVerificationSms: data['donor_verification_sms'],
    }

    let response = null
    try {
      response = await DonationApi.csc.createNAACPDonation(payload)
      if (handleThirdPartyDonationResponse(response)) {
        return
      }
      submissionStatus.value = response.status
      submissionId.value = response.message
      if (submissionStatus.value != 'success') {
        return handleQrOrGenericSubmitFailure(
          response,
          formatThirdPartyMessage(response.message) || 'Donation failed.',
        )
      }
      localStorage.removeItem('DonorData')
      localStorage.removeItem('campaign')
      localStorage.removeItem('qrCode')
      localStorage.removeItem('fundraiserId')
      donateSuccessMessage.value = formatTransactionIdMessage(submissionId.value)
      toggleSuccessModal()
      loading.value = false
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message?.[0] || error?.response?.data?.[0] || error.message
      console.error('Error creating donation:', errorMessage)
    }
    if (!submissionStatus.value) {
      return handleQrOrGenericSubmitFailure(
        response,
        'Please double check that all information is filled in correctly and try again.',
      )
    }
  }

  const onEngagingNetworkSubmitForm = async (data) => {
    loading.value = true
    const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }
    const payload = {
      firstName: donorData.donor.firstName,
      lastName: donorData.donor.lastName,
      dateOfBirth: donorData.donor.dateOfBirth,
      mobilePhone: donorData.donor.mobilePhone,
      homePhone: donorData.donor.homePhone || donorData.donor.mobilePhone,
      cardHolderName: `${data['billing.name.first']} ${data['billing.name.last']}`.trim(),
      mail: data['donor.email'],
      address: `${data['billing.address.street1']} ${data['billing.address.street2']}`.trim(),
      city: data['billing.address.city'],
      state: data['billing.address.state'],
      zip: data['billing.address.zip'],
      cardNumber: data['card_number'],
      cardCvv: data['card_cvv'],
      amount: data['otherAmount'],
      frequency: data['sustaining.frequency'].toUpperCase(),
      uniqueId: data['unique_id'],
      cardExpDateMonth: Number(data['card_exp_date_month']),
      cardExpDateYear: Number(data['card_exp_date_year']),
      emailUpdates: donorData.donor.emailUpdates,
      textMessages: donorData.donor.textMessages,
      originSource: donorData.donor.originSource,
      campaignId: campaignId,
      frequencyId: data['frequency'],
      formToken,
      confirmationCode: generateConfirmationCode(),
      qrCode: resolveQrCodeForPayload(),
      longitude: donorData.donor.longitude || 0,
      latitude: donorData.donor.latitude || 0,
      isEmailPreferred: donorData.donor.isEmailPreferred,
      isSmsPreferred: donorData.donor.isSmsPreferred,
      isPhoneCallPreferred: donorData.donor.isPhoneCallPreferred,
      donorVerificationSms: data['donor_verification_sms'],
    }

    let response = null
    try {
      response = await DonationApi.csc.createEngaingNetworkDonation(payload)
      if (handleThirdPartyDonationResponse(response)) {
        return
      }
      submissionStatus.value = response.status
      submissionId.value = response.transaction_id
      if (!submissionId.value) {
        return handleQrOrGenericSubmitFailure(
          response,
          response?.transaction_error_msg ||
            response?.transactionErrorMsg ||
            formatThirdPartyMessage(response?.message) ||
            'Donation failed.',
        )
      }
      localStorage.removeItem('DonorData')
      localStorage.removeItem('campaign')
      localStorage.removeItem('qrCode')
      localStorage.removeItem('fundraiserId')
      donateSuccessMessage.value = formatTransactionIdMessage(submissionId.value)
      toggleSuccessModal()
      loading.value = false
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message?.[0] || error?.response?.data?.[0] || error.message
      console.error('Error creating donation:', errorMessage)
    }
    if (!submissionId.value) {
      return handleQrOrGenericSubmitFailure(
        response,
        'Please double check that all information is filled in correctly and try again.',
      )
    }
  }

  /**
   * Blackbaud SKY Payments: the card is entered on our form and Django authorizes it with
   * Blackbaud before recording the gift in Luminate, so the result arrives in one call
   * with the same response shape as the other card processors.
   */
  const onBBMSSkyPaymentsSubmitForm = async (data) => {
    loading.value = true
    submissionId.value = null
    submissionStatus.value = null
    const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
    const donor = donorData.donor || {}
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }

    let captchaText = ''
    try {
      // reCAPTCHA tokens expire within minutes, so one is requested right before submit.
      captchaText = await executeRecaptcha(campaign.value?.latestPaymentProvider?.recaptchaSiteKey)
    } catch (e) {
      return handleFailure('SKY Payments reCAPTCHA', e, generateErrorMsg(e))
    }

    const payload = {
      campaign: campaignId,
      formToken,
      unique_id: data['unique_id'],
      frequency_id: data['frequency'],
      frequency: data['sustaining.frequency'],
      amount: data['otherAmount'],
      first_name: donor.firstName,
      last_name: donor.lastName,
      date_of_birth: donor.dateOfBirth,
      mail: data['donor.email'],
      mobile_phone: donor.mobilePhone || '',
      home_phone: donor.homePhone || '',
      address: data['billing.address.street1'],
      billing_address_street2: data['billing.address.street2'] || '',
      city: data['billing.address.city'],
      state: data['billing.address.state'],
      zip: data['billing.address.zip'],
      billing_name_first: data['billing.name.first'],
      billing_name_last: data['billing.name.last'],
      card_holder_name: `${data['billing.name.first']} ${data['billing.name.last']}`.trim(),
      card_number: data['card_number'],
      card_cvv: data['card_cvv'],
      card_exp_date_month: data['card_exp_date_month'],
      card_exp_date_year: data['card_exp_date_year'],
      email_opt_in: Boolean(data['donor.email_opt_in']),
      is_email_preferred: Boolean(donor.isEmailPreferred),
      is_sms_preferred: Boolean(donor.isSmsPreferred),
      is_phone_call_preferred: Boolean(donor.isPhoneCallPreferred),
      latitude: donor.latitude || 0,
      longitude: donor.longitude || 0,
      custom_questions: buildCustomQuestionAnswers(data.questionData),
      answers: mapDonationAnswers(data.questionData),
      captcha_text: captchaText,
      confirmation_code: generateConfirmationCode(),
      qr_code: resolveQrCodeForPayload(),
      donor_verification_sms: Boolean(data['donor_verification_sms']),
    }

    let response = null
    try {
      response = await DonationApi.csc.createBBMSSkyDonation(payload)
    } catch (error) {
      console.error('SKY Payments donation failed:', error)
      return handleQrOrGenericSubmitFailure(error?.response?.data, generateErrorMsg(error))
    }

    if (handleThirdPartyDonationResponse(response)) {
      return
    }
    submissionStatus.value = response?.status
    submissionId.value = response?.transaction_id
    if (submissionStatus.value !== 'success' || !submissionId.value) {
      return handleQrOrGenericSubmitFailure(
        response,
        response?.transaction_error_msg ||
          formatThirdPartyMessage(response?.message) ||
          'Donation failed.',
      )
    }
    clearDonorSessionStorage()
    donateSuccessMessage.value = formatTransactionIdMessage(submissionId.value)
    toggleSuccessModal()
    loading.value = false
  }

  const onSpringBoardSubmitForm = async (data) => {
    loading.value = true
    submissionId.value = null
    submissionStatus.value = null
    const donorData = JSON.parse(localStorage.getItem('DonorData')) || {}
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }
    const payload = {
      firstName: donorData.donor.firstName,
      lastName: donorData.donor.lastName,
      mail: data['donor.email'],
      address: `${data['billing.address.street1']} ${data['billing.address.street2']}`.trim(),
      cardHolderName: `${data['billing.name.first']} ${data['billing.name.last']}`.trim(),
      city: data['billing.address.city'],
      mobilePhone: donorData.donor.mobilePhone,
      state: data['billing.address.state'],
      zip: data['billing.address.zip'],
      cardNumber: data['card_number'],
      cardCvv: data['card_cvv'],
      amount: data['otherAmount'],
      frequency: data['sustaining.frequency'],
      uniqueId: data['unique_id'],
      cardExpDateMonth: Number(data['card_exp_date_month']),
      cardExpDateYear: Number(data['card_exp_date_year']),
      campaign: campaignId,
      frequencyId: data['frequency'],
      formToken,
      confirmationCode: generateConfirmationCode(),
      qrCode: resolveQrCodeForPayload(),
      longitude: donorData.donor.longitude || 0,
      latitude: donorData.donor.latitude || 0,
      isEmailPreferred: donorData.donor.isEmailPreferred,
      isSmsPreferred: donorData.donor.isSmsPreferred,
      isPhoneCallPreferred: donorData.donor.isPhoneCallPreferred,
      dateOfBirth: donorData.donor.dateOfBirth,
      donorVerificationSms: data['donor_verification_sms'],
    }

    let response = null
    try {
      response = await DonationApi.csc.createSpringBoardDonation(payload)
      if (handleThirdPartyDonationResponse(response)) {
        return
      }
      submissionStatus.value = response.status
      submissionId.value = response.submission_id
      if (!submissionId.value) {
        return handleQrOrGenericSubmitFailure(
          response,
          formatThirdPartyMessage(response.message) || 'Donation failed.',
        )
      }
      localStorage.removeItem('DonorData')
      localStorage.removeItem('campaign')
      localStorage.removeItem('qrCode')
      localStorage.removeItem('fundraiserId')
      donateSuccessMessage.value = formatTransactionIdMessage(submissionId.value)
      toggleSuccessModal()
      loading.value = false
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message?.[0] || error?.response?.data?.[0] || error.message

      console.error('Error creating donation:', errorMessage)
      console.error(error)
      donateFailureMessage.value =
        errorMessage ||
        'Please double check that all information is filled in correctly and try again.'
    }

    if (!submissionId.value) {
      return handleQrOrGenericSubmitFailure(
        response,
        'Please double check that all information is filled in correctly and try again.',
      )
    }
  }
  const buildBBMSCheckoutContext = (data) => {
    const { frequency, questionData, ...paymentData } = data
    const checkoutAmount = parseCheckoutAmount(data['otherAmount'])
    const donationFormData = {
      ...data,
      otherAmount: checkoutAmount,
      cardHolderName: `${data['billing.name.first']} ${data['billing.name.last']}`.trim(),
    }

    return {
      checkoutAmount,
      failureContext: {
        campaignId,
        donationFormData,
        frequency,
        fundraiserId,
        questionData,
        paymentData,
        qrCode,
      },
    }
  }

  /** Answers to the campaign's custom questions, keyed by their Luminate element name. */
  const buildCustomQuestionAnswers = (questionData) =>
    (questionData || []).reduce((acc, entry) => {
      const key = entry?.question?.apiValue
      const answer = entry?.question?.answers?.[0]?.text
      if (key && answer) acc[key] = answer
      return acc
    }, {})

  const getStoredDonor = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('DonorData')) || {}
      return stored.donor ?? stored ?? {}
    } catch {
      return {}
    }
  }

  const buildCheckoutParams = ({
    data,
    formId,
    levelId,
    transactionToken,
    checkoutAmount,
    captchaToken,
  }) => {
    const params = {
      form_id: formId,
      level_id: levelId,
      transaction_token: transactionToken,
      other_amount: checkoutAmount,
      'donor.email': data['donor.email'],
      'donor.email_opt_in': data['donor.email_opt_in'] ? 'true' : 'false',
      'billing.name.first': data['billing.name.first'],
      'billing.name.last': data['billing.name.last'],
      'billing.address.street1': data['billing.address.street1'],
      'billing.address.street2': data['billing.address.street2'] || '',
      'billing.address.city': data['billing.address.city'],
      'billing.address.state': data['billing.address.state'],
      'billing.address.zip': data['billing.address.zip'],
    }
    if (data['sustaining.frequency']) {
      params['sustaining.frequency'] = data['sustaining.frequency']
    }
    if (data['sustaining.duration']) {
      params['sustaining.duration'] = data['sustaining.duration']
    }
    if (captchaToken) {
      params['captcha_text'] = captchaToken
    }

    const donor = getStoredDonor()
    const phone = donor?.mobilePhone || donor?.homePhone || ''
    if (phone) {
      params['donor.phone'] = phone
    }
    params['sms_consent'] = Boolean(donor?.isSmsPreferred)

    const uniqueId = data['unique_id'] ?? data['uniqueId']
    if (uniqueId) {
      params['unique_id'] = uniqueId
    }

    // Django filters these against the campaign's configured question api_values before
    // passing anything on to Luminate.
    const customQuestions = buildCustomQuestionAnswers(data.questionData)
    if (Object.keys(customQuestions).length) {
      params['custom_questions'] = customQuestions
    }

    return params
  }

  /**
   * Records an authorized Blackbaud Checkout transaction against Luminate Online.
   * Shared by the modal and inline workflows: by this point a transaction token exists
   * and only the way it was collected differs.
   */
  const recordCheckoutDonation = async ({
    data,
    formId,
    levelId,
    transactionToken,
    checkoutAmount,
    failureContext,
  }) => {
    let donationResponse
    try {
      // reCAPTCHA tokens are only valid for a couple of minutes, so this happens here
      // rather than when the donor first reaches the payment step.
      const captchaToken = await executeRecaptcha(
        campaign.value?.latestPaymentProvider?.recaptchaSiteKey,
      )

      donationResponse = await submitDonateCheckout({
        campaign: campaignId,
        formToken,
        params: buildCheckoutParams({
          data,
          formId,
          levelId,
          transactionToken,
          checkoutAmount,
          captchaToken,
        }),
      })
    } catch (apiError) {
      return handleBBMSCheckoutFailure(generateErrorMsg(apiError), failureContext)
    }

    const transactionInfo = parseDonationResponse(donationResponse)

    if (!transactionInfo.transactionId && !transactionInfo.confirmationCode) {
      return handleBBMSCheckoutFailure(
        'Payment completed but no transaction ID was returned from Blackbaud.',
        failureContext,
      )
    }

    await saveBBMSCheckoutDonation(failureContext, {
      status: donationStatuses.SUCCESS,
      errorMessage: '',
      transactionInfo,
      isSuccess: true,
    })

    bbmsCheckoutFailedAttempts.value = 0
    bbmsCheckoutRetryMessage.value = ''

    clearDonorSessionStorage()
    donateSuccessMessage.value = formatTransactionIdMessage(transactionInfo.transactionId)
    toggleSuccessModal()
    loading.value = false
  }

  const onBBMSCheckoutSubmitForm = async (data) => {
    loading.value = true
    const provider = campaign.value.latestPaymentProvider
    const { formId, levelId, environmentId, paymentConfigurationId } = provider

    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }

    const { checkoutAmount, failureContext } = buildBBMSCheckoutContext(data)
    if (!Number.isFinite(checkoutAmount) || checkoutAmount <= 0) {
      return handleFailure(
        'BBMSCheckout donation failed',
        new Error('Invalid amount'),
        'Please select or enter a donation amount before submitting.',
      )
    }

    if (!donorId.value) {
      await createDonor()
    }
    if (!donorId.value) {
      loading.value = false
      return
    }

    try {
      try {
        await saveBBMSCheckoutDonation(failureContext, {
          status: donationStatuses.PENDING,
          errorMessage: 'Blackbaud checkout in progress',
          isSuccess: false,
        })
      } catch (pendingError) {
        console.error('Failed to save BBMS checkout pending record:', pendingError)
      }

      let transactionToken
      try {
        transactionToken = await openCheckoutModal({
          environmentId,
          paymentConfigurationId,
          amount: checkoutAmount,
          firstName: data['billing.name.first'],
          lastName: data['billing.name.last'],
          street1: data['billing.address.street1'],
          city: data['billing.address.city'],
          state: data['billing.address.state'],
          zip: data['billing.address.zip'],
          email: data['donor.email'],
        })
      } catch (e) {
        if (e.message === 'Payment was cancelled by the donor.') {
          closeActiveCheckoutModal()
          try {
            await saveBBMSCheckoutDonation(failureContext, {
              status: donationStatuses.FAILURE,
              errorMessage: 'Payment cancelled by the donor.',
              isSuccess: false,
            })
          } catch (cancelSaveError) {
            console.error('Failed to save cancelled BBMS checkout record:', cancelSaveError)
          }
          loading.value = false
          return
        }
        const checkoutErrorMessage = generateErrorMsg(e)
        return handleBBMSCheckoutFailure(checkoutErrorMessage, failureContext)
      }

      await recordCheckoutDonation({
        data,
        formId,
        levelId,
        transactionToken,
        checkoutAmount,
        failureContext,
      })
    } catch (error) {
      const errorMessage = generateErrorMsg(error)
      console.error('BBMSCheckout error:', errorMessage)
      return handleBBMSCheckoutFailure(errorMessage, failureContext)
    }
  }

  /**
   * Blackbaud's inline checkout authorizes the card as soon as the donor clicks the
   * mounted button, so the PENDING donation is written at click time rather than after
   * tokenization. This keeps the same record-first guarantee the modal workflow has.
   */
  const onBBMSCheckoutInlineStarted = async (data) => {
    loading.value = true
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }

    const { failureContext } = buildBBMSCheckoutContext(data)
    inlineFailureContext = failureContext

    inlinePendingSave = (async () => {
      if (!donorId.value) {
        await createDonor()
      }
      if (!donorId.value) return
      await saveBBMSCheckoutDonation(failureContext, {
        status: donationStatuses.PENDING,
        errorMessage: 'Blackbaud checkout in progress',
        isSuccess: false,
      })
    })().catch((pendingError) => {
      console.error('Failed to save BBMS inline checkout pending record:', pendingError)
    })

    await inlinePendingSave
  }

  const onBBMSCheckoutInlineSubmitForm = async (data) => {
    loading.value = true
    const { formId, levelId } = campaign.value.latestPaymentProvider

    if (inlinePendingSave) {
      await inlinePendingSave
      inlinePendingSave = null
    }
    if (!qrCode) {
      qrCode = localStorage.getItem('qrCode')
    }

    const { checkoutAmount, failureContext } = buildBBMSCheckoutContext(data)
    inlineFailureContext = failureContext

    if (!donorId.value) {
      await createDonor()
    }
    if (!donorId.value) {
      loading.value = false
      return
    }

    try {
      await recordCheckoutDonation({
        data,
        formId,
        levelId,
        transactionToken: data.transactionToken,
        checkoutAmount,
        failureContext,
      })
    } catch (error) {
      const errorMessage = generateErrorMsg(error)
      console.error('BBMSCheckoutInline error:', errorMessage)
      return handleBBMSCheckoutFailure(errorMessage, failureContext)
    }
  }

  /** Blackbaud reported a decline or SDK failure before we ever received a token. */
  const onBBMSCheckoutInlineError = async (message) => {
    if (!inlineFailureContext) {
      donateFailureMessage.value = message
      bbmsCheckoutRetryMessage.value = message
      loading.value = false
      return
    }
    await handleBBMSCheckoutFailure(message, inlineFailureContext)
  }

  const onSubmitForms = async (data) => {
    loading.value = true
    paymentProvider.value = campaign.value.latestPaymentProvider?.paymentProvider
    if (paymentProviders[paymentProvider.value] === SpringBoard) {
      await onSpringBoardSubmitForm(data)
      return
    }
    if (paymentProviders[paymentProvider.value] === EngagingNetwork) {
      await onEngagingNetworkSubmitForm(data)
      return
    }
    if (paymentProviders[paymentProvider.value] === NAACPCustomAPI) {
      await onNAACPCustomAPISubmitForm(data)
      return
    }
    if (paymentProviders[paymentProvider.value] === BBMSCheckout) {
      await onBBMSCheckoutSubmitForm(data)
      return
    }
    if (paymentProviders[paymentProvider.value] === BBMSCheckoutInline) {
      await onBBMSCheckoutInlineSubmitForm(data)
      return
    }
    if (paymentProviders[paymentProvider.value] === BBMSSkyPayments) {
      await onBBMSSkyPaymentsSubmitForm(data)
      return
    }
    const { formId, levelId, customDomain } = campaign?.value?.latestPaymentProvider
    const { frequency, questionData, ...paymentData } = data
    const additionalQuestions = questionData.reduce((acc, value) => {
      return { ...acc, [value.question.apiValue]: value.question.answers[0].text }
    }, {})
    let paymentDetails = {
      ...paymentData,
      additionalQuestions,
      formId,
      levelId,
      customDomain,
      mockPayment,
    }
    if (campaign.value.testMode) {
      paymentDetails.dfPreview = true
    }

    if (!donorId.value) {
      await createDonor()
    }
    // fillForm(data)
    const transactionInfo = await createTransaction(paymentDetails)

    await createDonation(
      campaignId,
      data,
      frequency,
      fundraiserId,
      questionData,
      transactionInfo,
      paymentData,
      qrCode,
    )

    if (!transactionInfo) {
      loading.value = false
      return toggleFailureModal()
    }

    donateSuccessMessage.value = formatTransactionIdMessage(transactionInfo.transactionId)
    toggleSuccessModal()
    localStorage.removeItem('DonorData')
    localStorage.removeItem('campaign')
    localStorage.removeItem('qrCode')
    localStorage.removeItem('fundraiserId')
    loading.value = false
  }

  const onSubmitFormsAlt = async (data, fundraiser, qrCode) => {
    // data = sample

    // get the selected frequency
    const freq = data.postVals.SelectedFrequency
    campaign.value = JSON.parse(localStorage.getItem('campaign'))
    // find its label in the data
    const label = data.formdef.form_elements
      .find((element) => element.name == 'ContributionInformation')
      .children.find((childElement) => childElement.name == 'SelectedFrequency')
      .options.find((option) => String(option.value) == String(freq)).display

    const frequency = campaign.value?.frequencies.find(
      (fr) => fr.value.toLowerCase() == label.toLowerCase(),
    )

    loading.value = true

    // PB - need to add a check here, if there is no levelid or formid then we should treat this as an inactive campaign!
    try {
      if (!donorId.value) {
        let userData = JSON.stringify({
          ...JSON.parse(localStorage.getItem('DonorData')),
          firstName: data.postVals.FirstName,
          lastName: data.postVals.LastName,
          email: data.postVals.EmailAddress,
          isEmailPreferred: data.postVals.YesSignMeUpForUpdatesForBinder,
          addressStreet: data.postVals.AddressLine1,
          city: data.postVals.City,
          state: data.postVals.StateProvince,
          postalCode: data.postVals.PostalCode,
          mobilePhone: data.postVals.MobilePhone
            ? `+${data.postVals.MobilePhoneInternationalDialingPrefix}${data.postVals.MobilePhone}`
            : '',
        })
        localStorage.setItem('DonorData', userData)
        await createDonor()
      }

      const donationInfo = {
        amount: data.response.contributionAmount,
        confirmationCode: data.response.confirmationID,
        transactionId: data.response.submissionId,
        campaign: campaign.value.id,
        donor: donorId.value,
        status: donationStatuses.SUCCESS,
        frequency: frequency?.id,
        formToken,
        qrCode: qrCode,
      }

      await DonationApi.csc.customCreate(donationInfo)

      loading.value = false
    } catch (e) {
      console.error(e)
    } finally {
      loading.value = false
    }
  }
  return {
    onSpringBoardSubmitForm,
    onSubmitForms,
    onSubmitFormsAlt,
    campaignId,
    fundraiserId,
    formToken,
    submissionStatus,
    submissionId,
    donorId,
    campaign,
    loading,
    getMaskedPan,
    toggleFailureModal,
    toggleSuccessModal,
    successModalOpen,
    failureModalOpen,
    donateSuccessMessage,
    donateFailureMessage,
    createDonor,
    termsAgreement,
    SpringBoard,
    EngagingNetwork,
    NAACPCustomAPI,
    BBMSCheckout,
    BBMSCheckoutInline,
    onBBMSCheckoutInlineStarted,
    onBBMSCheckoutInlineError,
    paymentProviders,
    paymentProvider,
    generateConfirmationCode,
    registerResetDonationForm,
    bbmsCheckoutRetryMessage,
  }
}
